import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { validateCoupon, calculateDiscountAmount, formatCouponDisplay } from '../utils/couponUtils';
import { calculateCartGST } from '../utils/gstUtils';
import toast from 'react-hot-toast';

const Cart = () => {
  const { cartItems, updateQuantity, removeFromCart, total, cartCount, subtotal, appliedCoupon, applyCoupon, removeCoupon, discount, cartLoading } = useCart();
  const { currentUser } = useAuth();
  const [couponCode, setCouponCode] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const handleQuantityChange = (id, selectedSize, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(id, selectedSize);
    } else {
      updateQuantity(id, selectedSize, newQuantity);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setApplyingCoupon(true);
    try {
      const result = await validateCoupon(couponCode.trim(), subtotal, currentUser?.uid);
      if (result.valid) {
        // Call context's applyCoupon, which now updates Firestore usage
        const applyResult = await applyCoupon(result.coupon);
          if (applyResult.success) {
            setCouponCode('');
            const saved = calculateDiscountAmount(result.coupon, subtotal);
            const savedFormatted = Math.round(saved).toLocaleString('en-IN');
            toast.success(`Coupon applied! You saved ₹${savedFormatted}`);
        } else {
          toast.error(applyResult.error || 'Failed to apply coupon');
        }
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Error applying coupon. Please try again.');
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    removeCoupon();
    toast.success('Coupon removed');
  };

  if (cartLoading) {
    return (
      <div className="min-h-screen bg-cream pt-20 pb-16">
        <div className="section-padding">
          <div className="container-max">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!Array.isArray(cartItems) || cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-cream pt-20 pb-16">
        <div className="section-padding">
          <div className="container-max">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <div className="mb-8">
                <svg className="mx-auto h-24 w-24 text-warm-gray" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l-1 12H6L5 9z" />
                </svg>
              </div>
              <h1 className="text-4xl font-serif text-deep-brown mb-4">Your Cart is Empty</h1>
              <p className="text-warm-gray mb-8 max-w-md mx-auto">
                Discover our exquisite collection of Indian and Western wear.
              </p>
              <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
                <Link
                  to="/indian-wear"
                  className="inline-block bg-deep-brown text-cream px-8 py-3 rounded-md hover:bg-warm-gray transition duration-300 font-medium"
                >
                  Shop Indian Wear
                </Link>
                <Link
                  to="/western-wear"
                  className="inline-block bg-gold text-deep-brown px-8 py-3 rounded-md hover:bg-yellow-500 transition duration-300 font-medium"
                >
                  Shop Western Wear
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate GST for the cart
  const gstAmount = calculateCartGST(total);

  return (
    <div className="min-h-screen bg-cream pt-20 pb-16">
      <div className="section-padding">
        <div className="container-max max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl font-serif text-deep-brown mb-8">Shopping Cart</h1>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-warm-gray/20">
                  <div className="px-6 py-4 border-b border-warm-gray/20">
                    <h2 className="text-lg font-serif font-medium text-deep-brown">
                      Cart Items ({cartCount})
                    </h2>
                  </div>
                  
                  <div className="divide-y divide-warm-gray/20">
                    {cartItems.map((item) => (
                      <div key={item.id} className="p-6 flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <img
                            src={item.images?.[0] || '/placeholder-image.jpg'}
                            alt={item.title}
                            className="h-20 w-20 object-cover rounded-md"
                          />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-serif font-medium text-deep-brown truncate">
                            {item.title}
                          </h3>
                          <p className="text-sm text-warm-gray">{item.brand}</p>
                          {item.selectedSize && (
                            <p className="text-sm text-warm-gray">Size: {item.selectedSize}</p>
                          )}
                          <div className="mt-2">
                            <span className="text-lg font-bold text-deep-brown">
                              ₹{item.discountedPrice || item.price}
                            </span>
                            {item.discountedPrice && (
                              <span className="ml-2 text-sm text-warm-gray line-through">
                                ₹{item.price}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => handleQuantityChange(item.id, item.selectedSize, item.quantity - 1)}
                            className="w-8 h-8 rounded-full border border-warm-gray/30 flex items-center justify-center hover:bg-cream transition duration-200"
                          >
                            -
                          </button>
                          <span className="w-8 text-center font-medium text-deep-brown">{item.quantity}</span>
                          <button
                            onClick={() => handleQuantityChange(item.id, item.selectedSize, item.quantity + 1)}
                            className="w-8 h-8 rounded-full border border-warm-gray/30 flex items-center justify-center hover:bg-cream transition duration-200"
                          >
                            +
                          </button>
                        </div>
                        
                        <button
                          onClick={() => removeFromCart(item.id, item.selectedSize)}
                          className="text-red-500 hover:text-red-700 transition duration-200"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24 border border-warm-gray/20">
                  <h2 className="text-lg font-serif font-medium text-deep-brown mb-4">Order Summary</h2>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-warm-gray">Subtotal ({cartCount} items)</span>
                      <span className="font-medium text-deep-brown">₹{subtotal.toLocaleString()}</span>
                    </div>
                    
                    {/* Applied Coupon Discount */}
                    {appliedCoupon && discount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span className="text-sm">
                          Coupon ({appliedCoupon.code}) - {formatCouponDisplay(appliedCoupon)}
                        </span>
                        <span className="font-medium">-₹{discount.toLocaleString()}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between">
                      <span className="text-warm-gray">Shipping</span>
                      <span className="text-sage">Free</span>
                    </div>
                    
                    <div className="border-t border-warm-gray/20 pt-3">
                      <div className="flex justify-between text-lg font-semibold">
                        <span className="text-deep-brown">Total</span>
                        <span className="text-deep-brown">₹{total.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-xs text-warm-gray mt-1">
                        <span>GST Included</span>
                        <span>₹{gstAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Coupon Code */}
                  <div className="mt-6">
                    {appliedCoupon ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-green-800">Coupon Applied!</p>
                            <p className="text-xs text-green-600">
                              {appliedCoupon.code} - {formatCouponDisplay(appliedCoupon)}
                            </p>
                          </div>
                          <button
                            onClick={handleRemoveCoupon}
                            className="text-red-500 hover:text-red-700 text-sm font-medium"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <label className="block text-sm font-medium text-deep-brown mb-2">
                          Coupon Code
                        </label>
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            placeholder="Enter coupon code"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                            className="flex-1 px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold"
                            disabled={applyingCoupon}
                          />
                          <button 
                            onClick={handleApplyCoupon}
                            disabled={applyingCoupon || !couponCode.trim()}
                            className="px-4 py-2 bg-deep-brown text-cream rounded-md hover:bg-warm-gray transition duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {applyingCoupon ? 'Applying...' : 'Apply'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Checkout Button */}
                  <Link
                    to="/checkout"
                    className="mt-6 w-full bg-gold text-deep-brown px-6 py-3 rounded-md font-semibold hover:bg-yellow-500 transition duration-300 text-center block"
                  >
                    Proceed to Checkout
                  </Link>
                  
                  {/* Continue Shopping */}
                  <Link
                    to="/"
                    className="mt-3 w-full border border-deep-brown text-deep-brown px-6 py-3 rounded-md font-medium hover:bg-deep-brown hover:text-cream transition duration-300 text-center block"
                  >
                    Continue Shopping
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Cart;